import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, Marker, DirectionsRenderer, InfoWindow, Rectangle } from '@react-google-maps/api';
import { getMapOptions } from '../../utils/mapUtils';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { authenticatedFetch } from '../../utils/auth';
import { generateUnifiedPDF, generateSingleLocationPDF, generateRoutePDF } from '../../utils/pdfGenerator';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const InteractiveMap = ({ 
  markerPositions = [], 
  clickable = false, 
  onMapClick, 
  defaultCenter = { lat: 20.5937, lng: 78.9629 }, 
  zoom = 5,
  mapType = 'roadmap',
  satelliteLabels = true,
  directions = null,
  bounds = null,
  routeDistanceInfo = null,
  onNearestLocationClick = null,
  onSpecificLocationClick = null,
  showInfoWindows = false,
  userEmail = null,
  // New props for location management
  onAddToMyLocation = null,
  onEditLocation = null,
  onDeleteLocation = null,
  // New prop for De-Dup marker clicks
  onDedupMarkerClick = null,
  // New prop for pure state rectangle management
  dedupRectangles = [],
  // New prop to force rectangle re-render when clearing
  dedupRectanglesKey = 0
}) => {
  // Add CSS to hide default Google Maps close button
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .gm-style .gm-style-iw-c .gm-style-iw-d .gm-style-iw-t::after,
      .gm-style .gm-style-iw-c .gm-style-iw-d .gm-style-iw-t::before,
      .gm-style .gm-style-iw-c .gm-style-iw-d .gm-style-iw-t .gm-style-iw-b,
      .gm-style .gm-style-iw-c .gm-style-iw-d .gm-style-iw-t {
        display: none !important;
      }
      .gm-style .gm-style-iw-c .gm-style-iw-d .gm-style-iw-t {
        background: none !important;
        box-shadow: none !important;
      }
      .gm-style .gm-style-iw-c .gm-style-iw-d {
        padding: 0 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Force cleanup of rectangles when dedupRectangles is cleared
  useEffect(() => {
    if (dedupRectangles.length === 0 && mapRef.current) {
      console.log('🧹 Force cleanup: dedupRectangles is empty, forcing map re-render');
      
      // Explicitly remove all tracked rectangle instances
      rectangleInstancesRef.current.forEach(rectangle => {
        if (rectangle && rectangle.setMap) {
          console.log('🗑️ Removing rectangle instance from map');
          rectangle.setMap(null);
        }
      });
      rectangleInstancesRef.current.clear();
      
      // Force a map re-render by triggering a resize event
      if (window.google && window.google.maps) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
    }
  }, [dedupRectangles.length, dedupRectanglesKey]);
  const mapRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const rectangleInstancesRef = useRef(new Set()); // Track Rectangle instances for cleanup
  // Unified state management for all marker info window visibility
  const [closedInfoWindows, setClosedInfoWindows] = useState(new Set());
  const [openedInfoWindows, setOpenedInfoWindows] = useState(new Set());
  const [userClickedMarkers, setUserClickedMarkers] = useState(new Set());
  const [currentZoom, setCurrentZoom] = useState(zoom);

  // Unified marker identification function
  const getMarkerKey = (position) => {
    if (position.isMyLocation) {
      return `my-location-${position.info.id}`;
    } else if (position.isCurrentMarker) {
      return `current-${position.lat}-${position.lng}`;
    } else if (position.isRouteMarker) {
      return `route-${position.lat}-${position.lng}`;
    } else if (position.isRouteInfo) {
      return 'route-info';
    } else if (position.isDedupMarker) {
      return `dedup-${position.lat}-${position.lng}`;
    }
    return `other-${position.lat}-${position.lng}`;
  };

  // Loading states for PDF generation
  const [pdfLoadingStates, setPdfLoadingStates] = useState({
    singleLocation: false,
    routePDF: false
  });

  // PDF Generation functions
  const generateSingleLocationPDF = async (markerData) => {
    setPdfLoadingStates(prev => ({ ...prev, singleLocation: true }));
    try {
      const singlePointData = {
        address: markerData.address,
        digipin: markerData.digipin,
        lat: markerData.lat,
        lng: markerData.lng,
        search_input: markerData.address || `${markerData.lat},${markerData.lng}`,
        source: 'pin2point_main',
        currentZoom: currentZoom // Pass the current zoom level from interactive map
      };

      console.log('📏 InteractiveMap PDF generation - using zoom level:', currentZoom);

      await generateUnifiedPDF(singlePointData, {
        type: 'single',
        userEmail
      });
    } catch (error) {
      console.error('Error generating single location PDF:', error);
    } finally {
      setPdfLoadingStates(prev => ({ ...prev, singleLocation: false }));
    }
  };

  const generateRoutePDF = async (markerData, routeInfo) => {
    setPdfLoadingStates(prev => ({ ...prev, routePDF: true }));
    try {
      // Extract current location from the first marker (current marker)
      const currentMarker = markerPositions.find(m => m.isCurrentMarker);
      
      // Extract route path from directions if available
      let routePath = null;
      if (directions && directions.routes && directions.routes.length > 0) {
        const route = directions.routes[0];
        if (route.overview_path) {
          // Convert LatLng objects to coordinate arrays [lat, lng] format for backend
          routePath = route.overview_path.map(point => [
            point.lat(),
            point.lng()
          ]);
          console.log('Route path extracted for PDF:', routePath.length, 'points');
        }
      }
      
      const routeData = {
        fromData: {
          address: currentMarker?.info?.address || 'Selected Location',
          digipin: currentMarker?.info?.digipin || 'N/A',
          lat: currentMarker?.lat || markerData.lat,
          lng: currentMarker?.lng || markerData.lng
        },
        toData: {
          address: markerData.address,
          digipin: markerData.digipin,
          lat: markerData.lat,
          lng: markerData.lng
        },
        distanceData: {
          road_distance_km: routeInfo?.roadDistance || 'N/A',
          aerial_distance_km: routeInfo?.aerialDistance || 'N/A',
          road_duration: routeInfo?.roadDuration || 'N/A'
        },
        routePath: routePath,
        source: 'pin2point_nearest_location'
      };

      await generateUnifiedPDF(routeData, {
        type: 'route',
        userEmail
      });
    } catch (error) {
      console.error('Error generating route PDF:', error);
    } finally {
      setPdfLoadingStates(prev => ({ ...prev, routePDF: false }));
    }
  };

  
  // Calculate smart offset position for route distance info using actual route path
  const getRouteInfoPosition = (routeDistanceInfo) => {
    if (!routeDistanceInfo) return null;
    
    // If we have directions data, use route-aware positioning
    if (directions && directions.routes && directions.routes.length > 0) {
      return getSmartRouteInfoPosition(routeDistanceInfo, directions);
    }
    
    // Fallback to simple offset positioning
    const baseLatOffset = 0.0005;
    const baseLngOffset = 0.002;
    const zoomFactor = Math.max(1, currentZoom - 10);
    const latOffset = baseLatOffset / zoomFactor;
    const lngOffset = baseLngOffset / zoomFactor;
    return {
      lat: routeDistanceInfo.position.lat + latOffset,
      lng: routeDistanceInfo.position.lng + lngOffset,
      arrowDirection: 'left' // Default arrow direction for fallback
    };
  };

  // Get true midpoint along the actual route path
  const getRoutePathMidpoint = (path) => {
    if (!path || path.length === 0) return null;
    
    // Find the middle point in the route path
    const midIndex = Math.floor(path.length / 2);
    const midPoint = path[midIndex];
    
    return {
      lat: midPoint.lat(),
      lng: midPoint.lng()
    };
  };

  // Calculate the general direction of the route
  const calculateRouteDirection = (path) => {
    if (!path || path.length < 2) return { isNorthSouth: true, isEastWest: false };
    
    const start = path[0];
    const end = path[path.length - 1];
    
    const latDiff = Math.abs(end.lat() - start.lat());
    const lngDiff = Math.abs(end.lng() - start.lng());
    
    // Determine if route is primarily north-south or east-west
    const isNorthSouth = latDiff > lngDiff;
    const isEastWest = lngDiff > latDiff;
    
    return { isNorthSouth, isEastWest };
  };

  // Calculate perpendicular offset based on route direction and characteristics
  const calculatePerpendicularOffset = (routeDirection, routeDistanceInfo) => {
    const { isNorthSouth, isEastWest } = routeDirection;
    
    // Base offset values (in degrees)
    let latOffset = 0;
    let lngOffset = 0;
    let arrowDirection = 'left'; // Default arrow direction
    
    // Adjust offset based on route distance
    const routeLength = routeDistanceInfo.roadDistance || 0;
    let offsetMultiplier = 1;
    
    if (routeLength < 5) {
      offsetMultiplier = 0.5; // Smaller offset for short routes
    } else if (routeLength > 20) {
      offsetMultiplier = 1.5; // Larger offset for long routes
    }
    
    // Base offset values
    const baseOffset = 0.001 * offsetMultiplier;
    
    if (isNorthSouth) {
      // Route runs north-south, place info window east or west
      lngOffset = baseOffset; // Place to the east
      arrowDirection = 'left'; // Arrow points left (west) towards route
    } else if (isEastWest) {
      // Route runs east-west, place info window north or south
      latOffset = baseOffset; // Place to the north
      arrowDirection = 'down'; // Arrow points down (south) towards route
    } else {
      // Mixed direction, use diagonal offset
      latOffset = baseOffset * 0.7;
      lngOffset = baseOffset * 0.7;
      arrowDirection = 'left'; // Default to left arrow
    }
    
    return { lat: latOffset, lng: lngOffset, arrowDirection };
  };

  // Smart route-aware positioning function
  const getSmartRouteInfoPosition = (routeDistanceInfo, directions) => {
    try {
      const route = directions.routes[0];
      if (!route || !route.overview_path) {
        console.log('No route path available, using fallback positioning');
        // Fallback to simple positioning
        return {
          lat: routeDistanceInfo.position.lat + 0.0005,
          lng: routeDistanceInfo.position.lng + 0.002
        };
      }
      
      const path = route.overview_path;
      
      // Get true midpoint along route path
      const routeMidpoint = getRoutePathMidpoint(path);
      if (!routeMidpoint) {
        console.log('Could not calculate route midpoint, using fallback positioning');
        // Fallback to simple positioning
        return {
          lat: routeDistanceInfo.position.lat + 0.0005,
          lng: routeDistanceInfo.position.lng + 0.002
        };
      }
      
      // Calculate route direction
      const routeDirection = calculateRouteDirection(path);
      
      // Calculate perpendicular offset
      const offset = calculatePerpendicularOffset(routeDirection, routeDistanceInfo);
      
      // Apply offset to route midpoint
      const finalPosition = {
        lat: routeMidpoint.lat + offset.lat,
        lng: routeMidpoint.lng + offset.lng,
        arrowDirection: offset.arrowDirection
      };
      
      console.log('Smart route positioning:', {
        routeMidpoint,
        routeDirection,
        offset,
        finalPosition,
        routeLength: routeDistanceInfo.roadDistance
      });
      
      return finalPosition;
      
    } catch (error) {
      console.log('Error in smart route positioning, using fallback:', error);
      // Fallback to simple positioning with dynamic zoom-based offset
      const baseLatOffset = 0.0005;
      const baseLngOffset = 0.002;
      const zoomFactor = Math.max(1, currentZoom - 10);
      const latOffset = baseLatOffset / zoomFactor;
      const lngOffset = baseLngOffset / zoomFactor;
      return {
        lat: routeDistanceInfo.position.lat + latOffset,
        lng: routeDistanceInfo.position.lng + lngOffset
      };
    }
  };


  
  // Unified click handler for all marker types
  const handleMarkerClick = (position) => {
    const markerKey = getMarkerKey(position);
    
    // Handle De-Dup marker clicks
    if (position.isDedupMarker && onDedupMarkerClick) {
      onDedupMarkerClick(position);
      return; // Don't show info window for De-Dup markers
    }
    
    // Mark as user-clicked
    setUserClickedMarkers(prev => new Set([...prev, markerKey]));
    
    // Add to opened info windows
    setOpenedInfoWindows(prev => new Set([...prev, markerKey]));
    
    if (closedInfoWindows.has(markerKey)) {
      // Reopen the info window
      setClosedInfoWindows(prev => {
        const newSet = new Set(prev);
        newSet.delete(markerKey);
        return newSet;
      });
    }
  };

  // Unified info window visibility check
  const shouldShowInfoWindow = (position) => {
    const markerKey = getMarkerKey(position);
    // Info windows are hidden by default unless explicitly opened by user
    return userClickedMarkers.has(markerKey) && !closedInfoWindows.has(markerKey);
  };

  // Unified close handler
  const handleInfoWindowClose = (position) => {
    const markerKey = getMarkerKey(position);
    setClosedInfoWindows(prev => new Set([...prev, markerKey]));
    setUserClickedMarkers(prev => {
      const newSet = new Set(prev);
      newSet.delete(markerKey);
      return newSet;
    });
  };


  


  const googleMapsState = useGoogleMaps();
  
  // Handle the case where isLoaded might be an object or boolean
  const isLoaded = typeof googleMapsState.isLoaded === 'boolean' ? googleMapsState.isLoaded : false;
  const loadError = googleMapsState.loadError;
  const isLoading = typeof googleMapsState.isLoading === 'boolean' ? googleMapsState.isLoading : false;

  // Check if Google Maps is actually available
  const googleMapsAvailable = !!(window.google && window.google.maps);
  
  // Force isLoaded to true if Google Maps is actually available
  const actualIsLoaded = isLoaded || googleMapsAvailable;
  const actualIsLoading = isLoading && !googleMapsAvailable;



  // Move all useCallback hooks to the top, before any conditional returns
  const onLoad = useCallback((map) => {
    console.log('GoogleMap onLoad called, map:', map);
    mapRef.current = map;
    
    if (bounds) {
      map.fitBounds(bounds, 100);
    } else if (defaultCenter) {
      map.panTo(defaultCenter);
    }
    
    // Initialize DirectionsRenderer
    if (window.google && window.google.maps) {
      try {
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          suppressMarkers: true,
          map: map
        });
      } catch (error) {
        // Error handling without console.log
      }
    }
    
    // Disable Google's default POI click behavior to ensure our custom handler takes precedence
    if (clickable) {
      map.setOptions({
        clickableIcons: true
      });
      
      // Add a listener to prevent default POI info windows
      const listener = map.addListener('click', (event) => {
        if (event.placeId) {
          console.log('Preventing default POI info window for placeId:', event.placeId);
          // The event.stop() in handleMapClick should handle this, but this is a backup
        }
      });
      
      // Store the listener for cleanup
      mapRef.current.poiClickListener = listener;
    }
  }, [defaultCenter, bounds, clickable]);

  const onUnmount = useCallback(() => {
    // Clean up POI click listener
    if (mapRef.current && mapRef.current.poiClickListener) {
      window.google.maps.event.removeListener(mapRef.current.poiClickListener);
    }
    mapRef.current = null;
  }, []);

  const onZoomChanged = useCallback(() => {
    if (mapRef.current) {
      setCurrentZoom(mapRef.current.getZoom());
    }
  }, []);

  const handleMapClick = useCallback(async (event) => {
    if (!onMapClick) {
      return;
    }
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    let clickData = { lat, lng };
    

    
    // Check if this is a POI click
    if (event.placeId) {
      console.log('POI click detected with placeId:', event.placeId);
      clickData.placeId = event.placeId;
      
      // Prevent Google's default info window by stopping event propagation
      event.stop();
      console.log('Prevented default POI info window, using custom handler');
      
      // Use Places API (New) to get detailed place information

      
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          if (window.google.maps.places.Place) {
            const place = new window.google.maps.places.Place({
              id: event.placeId,
              requestedLanguage: 'en'
            });
            
            const placeResult = await place.fetchFields({
              fields: ['displayName', 'formattedAddress', 'location', 'types', 'photos']
            });
            
            console.log('Raw placeResult:', placeResult);
            console.log('placeResult.place:', placeResult.place);
            
            if (placeResult) {
              console.log('Places API (New) result:', placeResult);
              console.log('placeResult.displayName:', placeResult.displayName);
              console.log('placeResult.formattedAddress:', placeResult.formattedAddress);
              console.log('placeResult.types:', placeResult.types);
              
              // Get place data from the result - the data is nested in placeResult.place
              const placeData = placeResult.place;
              
              // Access the data using the getter methods
              // Try different ways to access displayName based on API version
              let placeName = '';
              if (placeData.displayName) {
                if (typeof placeData.displayName === 'string') {
                  placeName = placeData.displayName;
                } else if (placeData.displayName.text) {
                  placeName = placeData.displayName.text;
                } else if (placeData.displayName.value) {
                  placeName = placeData.displayName.value;
                }
              }
              
              const formattedAddress = placeData.formattedAddress || '';
              const placeTypes = placeData.types || [];
              const photos = placeData.photos || [];
              
              console.log('Extracted place data:', { placeName, formattedAddress, placeTypes });
              console.log('displayName structure:', placeData.displayName);
              console.log('displayName.text:', placeData.displayName?.text);
              console.log('displayName direct:', placeData.displayName);
              
              const enhancedClickData = {
                ...clickData,
                placeName: placeName,
                formattedAddress: formattedAddress,
                placeTypes: placeTypes,
                photos: photos
              };
              console.log('Enhanced click data:', enhancedClickData);
              onMapClick(enhancedClickData);
              return;
            }
          }
        } catch (error) {
          console.log('Places API (New) error for POI click:', error);
          // Continue to basic click data
        }
      } else {
        console.log('Places API not available - using basic click data');
      }
      
      // Fallback: pass basic click data with placeId
      onMapClick(clickData);
    } else {
      // Regular map click - pass basic click data immediately for instant feedback
      onMapClick(clickData);
    }
  }, [onMapClick]);

  // Handle bounds changes
  useEffect(() => {
    if (mapRef.current && bounds) {
      mapRef.current.fitBounds(bounds, 100);
    }
  }, [bounds]);

  // Handle directions changes
  useEffect(() => {
    if (mapRef.current && directions) {
      try {
        directionsRendererRef.current.setDirections(directions);
      } catch (error) {
        // Error handling without console.log
      }
    } else if (directionsRendererRef.current) {
      directionsRendererRef.current.setDirections({});
    }
  }, [directions]);



  if (loadError) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%', 
        backgroundColor: '#f8f9fa',
        color: '#666',
        fontSize: '16px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p>Error loading Google Maps.</p>
          <p style={{ fontSize: '14px', marginTop: '10px' }}>
            Please check your internet connection and try again.
          </p>
        </div>
      </div>
    );
  }



  // Always render the map if Google Maps is available, regardless of loading state
  if (!googleMapsAvailable) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%', 
        backgroundColor: '#f8f9fa',
        color: '#666',
        fontSize: '16px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p>Loading map...</p>
          <p style={{ fontSize: '14px', marginTop: '10px' }}>
            Please wait while we load the map.
          </p>
          <p style={{ fontSize: '12px', marginTop: '5px', color: '#999' }}>
            Debug: googleAvailable={googleMapsAvailable.toString()}
          </p>
        </div>
      </div>
    );
  }

  // Get map options and ensure POI clicks are handled by our custom handler
  const mapOptions = getMapOptions(mapType, satelliteLabels);
  mapOptions.clickableIcons = true; // Ensure POIs are clickable

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={markerPositions && markerPositions.length > 0 && !bounds ? markerPositions[0] : defaultCenter}
      zoom={zoom}
      onClick={clickable ? handleMapClick : undefined}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onZoomChanged={onZoomChanged}
      options={mapOptions}
    >
      {markerPositions && markerPositions.map((position, index) => {
        // Skip current markers - they are handled separately at the end
        if (position.isCurrentMarker) {
          return null;
        }
        
        // Show De-Dup markers on top-left edge of rectangle with conditional colors
        if (position.isDedupMarker) {
                      // Calculate top-left position of the rectangle, slightly to the left
            const rect = dedupRectangles && dedupRectangles.length > 0 ? dedupRectangles[0] : null;
            if (rect && rect.bounds) {
              // Move marker slightly to the left of the rectangle to avoid click conflicts
              const offset = 0.0001; // Small offset to the left
              const topLeftPosition = {
                lat: rect.bounds.north,
                lng: rect.bounds.west - offset
              };
            
            // Use same conditional colors as rectangle
            const count = position.count || 0;
            let markerColor;
            if (count === 0) markerColor = '#28a745'; // Green
            else if (count >= 1 && count < 5) markerColor = '#fd7e14'; // Orange
            else if (count >= 5 && count < 10) markerColor = '#6f42c1'; // Purple
            else markerColor = '#dc3545'; // Red (10+ searches)
            
            // Create custom SVG icon for De-Dup marker with count
            const countText = count > 99 ? '99+' : count.toString();
            const svgIcon = {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="${markerColor}" stroke="white" stroke-width="2"/>
                  <text x="12" y="16" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="8" font-weight="bold">${countText}</text>
                </svg>
              `)}`,
              scaledSize: new window.google.maps.Size(32, 32),
              anchor: new window.google.maps.Point(16, 16)
            };
            
            return (
              <Marker
                key={`dedup-${index}`}
                position={topLeftPosition}
                icon={svgIcon}
                title={position.title || `De-Dup: ${count} searches`}
                onClick={() => handleMarkerClick(position)}
                options={{
                  cursor: 'pointer'
                }}
              />
            );
          }
          return null;
        }
        
        // Debug: Log marker properties
        if (index === 0) {
          console.log('All markers in InteractiveMap:', markerPositions.map(m => ({
            title: m.title,
            isCurrentMarker: m.isCurrentMarker,
            isMyLocation: m.isMyLocation,
            isRouteMarker: m.isRouteMarker,
            isDedupMarker: m.isDedupMarker,
            count: m.count
          })));
        }
        
        // Determine marker color based on properties
        let markerColor = '#FF0000'; // Default red
        
        // Debug: Log marker properties for troubleshooting
        console.log(`Marker ${index} properties:`, {
          title: position.title,
          isMyLocation: position.isMyLocation,
          isRouteMarker: position.isRouteMarker,
          isCurrentMarker: position.isCurrentMarker,
          isDedupMarker: position.isDedupMarker,
          isDefault: position.isDefault,
          isDestination: position.isDestination
        });
        
        // My location markers always use their specific colors, regardless of other properties
        if (position.isMyLocation) {
          if (position.isDefault) {
            markerColor = '#FFD700'; // Yellow for default location
          } else {
            markerColor = '#007bff'; // Blue for regular my location markers
          }
        } else if (position.isCurrentMarker) {
          // Single location markers (search results, map clicks)
          markerColor = '#FF0000'; // Red for single location markers
        } else if (position.isRouteMarker) {
          // Route markers (only for non-my-location markers)
          if (position.isDestination) {
            markerColor = '#34A853'; // Green for destination
          } else {
            markerColor = '#FF0000'; // Red for origin
          }
        } else if (position.isDedupMarker) {
          // De-Dup markers - use purple color
          markerColor = '#9C27B0'; // Purple for De-Dup markers
        }

        // Create custom SVG icon with proper Google Maps pin shape
        let svgIcon;
        
        if (position.isDedupMarker) {
          // Special icon for De-Dup markers with count
          const count = position.count || 0;
          const countText = count > 99 ? '99+' : count.toString();
          svgIcon = {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="48" height="48" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="${markerColor}" stroke="white" stroke-width="2"/>
                <text x="12" y="16" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="10" font-weight="bold">${countText}</text>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(48, 48),
            anchor: new window.google.maps.Point(24, 24)
          };
        } else {
          // Regular pin icon for other markers
          svgIcon = {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="48" height="48" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${markerColor}"/>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(48, 48),
            anchor: new window.google.maps.Point(24, 48)
          };
        }

        console.log(`Rendering marker ${index}:`, position.title, position);
        return (
          <Marker
            key={index}
            position={position}
            icon={svgIcon}
            title={position.title || `Marker ${index + 1}`}
            onClick={() => handleMarkerClick(position)}
            // Add cursor pointer to indicate clickability
            options={{
              cursor: 'pointer'
            }}
          />
        );
      })}
      
      {directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#4285F4',
              strokeOpacity: 1,
              strokeWeight: 10
            }
          }}
                  />
        )}
      
      {/* De-Dup Area Rectangle - Single Rectangle Approach */}
      {dedupRectangles && dedupRectangles.length > 0 && dedupRectangles[0].bounds && 
        dedupRectangles[0].bounds.north > dedupRectangles[0].bounds.south && 
        dedupRectangles[0].bounds.east > dedupRectangles[0].bounds.west && (
          <Rectangle
            
            key={`dedup-rect-level-${dedupRectangles[0].level}-key-${dedupRectanglesKey}`}
            bounds={{
              north: Math.max(dedupRectangles[0].bounds.north, dedupRectangles[0].bounds.south),
              south: Math.min(dedupRectangles[0].bounds.north, dedupRectangles[0].bounds.south),
              east: Math.max(dedupRectangles[0].bounds.east, dedupRectangles[0].bounds.west),
              west: Math.min(dedupRectangles[0].bounds.east, dedupRectangles[0].bounds.west)
            }}
            options={{
              fillColor: (() => {
                const count = dedupRectangles[0].count || 0;
                if (count === 0) return '#28a745'; // Green
                if (count >= 1 && count < 5) return '#fd7e14'; // Orange
                if (count >= 5 && count < 10) return '#6f42c1'; // Purple
                return '#dc3545'; // Red (10+ searches)
              })(),
              fillOpacity: 0.4,
              strokeColor: (() => {
                const count = dedupRectangles[0].count || 0;
                if (count === 0) return '#28a745'; // Green
                if (count >= 1 && count < 5) return '#fd7e14'; // Orange
                if (count >= 5 && count < 10) return '#6f42c1'; // Purple
                return '#dc3545'; // Red (10+ searches)
              })(),
              strokeOpacity: 1.0,
              strokeWeight: 5,
              clickable: false
            }}
            onLoad={(rectangle) => {
              rectangleInstancesRef.current.add(rectangle);
            }}
            onUnmount={(rectangle) => {
              rectangleInstancesRef.current.delete(rectangle);
            }}
            
          />
        )
      }
      
      {/* Info Windows for markers (excluding current markers) */}
      {markerPositions && markerPositions.map((position, index) => {
        if (!position.info || position.isCurrentMarker) {
          return null;
        }
        
        // Check if info window should be shown using unified function
        if (!shouldShowInfoWindow(position)) {
          return null;
        }
        
        // Calculate offset position for each marker's info window
        const getInfoWindowPosition = (markerPosition) => {
          // Calculate dynamic offset based on zoom level
          // Higher zoom = smaller offset, lower zoom = larger offset
          const baseOffset = 0.0005;
          const zoomFactor = Math.max(1, currentZoom - 10); // Adjust based on your needs
          const dynamicOffset = baseOffset / zoomFactor;
          
          return {
            lat: markerPosition.lat + dynamicOffset,
            lng: markerPosition.lng
          };
        };
         
         // For My Location markers, show info window only when clicked
         if (position.isMyLocation) {
           return (
             <InfoWindow
               key={`info-${index}`}
               position={getInfoWindowPosition(position)}
               onCloseClick={() => handleInfoWindowClose(position)}
             >
            <div style={{ 
              padding: '8px', 
              maxWidth: '220px',
              backgroundColor: '#fff',
              borderRadius: '6px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              border: position.isDefault ? '2px solid #FFD700' :
                      showInfoWindows ? '2px solid #007bff' :
                      position.isCurrentMarker ? '2px solid #4285F4' : 
                      position.isRouteMarker ? '2px solid #34A853' : 
                      '2px solid #FF0000'
            }}>
              <h4 style={{ 
                margin: '0 0 6px 0', 
                color: position.isDefault ? '#FFD700' :
                       showInfoWindows ? '#007bff' :
                       position.isCurrentMarker ? '#4285F4' : 
                       position.isRouteMarker ? '#34A853' : '#FF0000',
                fontSize: '13px',
                fontWeight: 'bold'
              }}>
                {showInfoWindows ? position.info.label : position.title}
              </h4>
              <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
                <div><strong>Address:</strong> {position.info.address}</div>
                {position.info.digipin && position.info.digipin !== 'N/A' && (
                  <div><strong>Digipin:</strong> {position.info.digipin}</div>
                )}
                <div><strong>Coordinates:</strong> {position.info.coordinates}</div>
                {position.info.isDefault && (
                  <div style={{ marginTop: '4px', padding: '2px 6px', backgroundColor: '#e7f3ff', borderRadius: '3px', fontSize: '10px', color: '#007bff', fontWeight: 'bold' }}>
                    ⭐ Default Location
                  </div>
                )}
              </div>
              {/* Button Group for My Location Markers */}
              {position.isMyLocation && (onEditLocation || onDeleteLocation) && (
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {/* Edit and Delete Buttons Row */}
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    {onEditLocation && (
                      <button
                        onClick={() => onEditLocation(position.info)}
                        style={{
                          backgroundColor: '#ffc107',
                          color: '#212529',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          transition: 'background-color 0.2s',
                          flex: 1
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#e0a800'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#ffc107'}
                      >
                        Edit
                      </button>
                    )}
                    {onDeleteLocation && (
                      <button
                        onClick={() => onDeleteLocation(position.info)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          transition: 'background-color 0.2s',
                          flex: 1
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
             </InfoWindow>
           );
         }
         
         // For Other markers (route markers, etc.), show info window with icons
         return (
           <InfoWindow
             key={`info-${position.lat}-${position.lng}`}
             position={getInfoWindowPosition(position)}
             onCloseClick={() => handleInfoWindowClose(position)}
           >
           <div style={{ 
             padding: '4px 8px 8px 8px', 
             maxWidth: '220px',
             backgroundColor: '#fff',
             borderRadius: '6px',
             boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
             border: position.isDefault ? '2px solid #FFD700' :
                     showInfoWindows ? '2px solid #007bff' :
                     position.isCurrentMarker ? '2px solid #4285F4' : 
                     position.isRouteMarker ? '2px solid #34A853' : 
                     '2px solid #FF0000'
           }}>
               <h4 style={{ 
                 margin: '0 0 6px 0', 
                 color: position.isDefault ? '#FFD700' :
                        showInfoWindows ? '#007bff' :
                        position.isCurrentMarker ? '#4285F4' : 
                        position.isRouteMarker ? '#34A853' : '#FF0000',
                 fontSize: '13px',
                 fontWeight: 'bold'
               }}>
                 {showInfoWindows ? position.info.label : position.title}
               </h4>
               <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
                 <div><strong>Address:</strong> {position.info.address}</div>
                 {position.info.digipin && position.info.digipin !== 'N/A' && (
                   <div><strong>Digipin:</strong> {position.info.digipin}</div>
                 )}
                 <div><strong>Coordinates:</strong> {position.info.coordinates}</div>
                 {position.info.isDefault && (
                   <div style={{ marginTop: '4px', padding: '2px 6px', backgroundColor: '#e7f3ff', borderRadius: '3px', fontSize: '10px', color: '#007bff', fontWeight: 'bold' }}>
                     ⭐ Default Location
                   </div>
                 )}
               </div>
               {/* Icon Row at Bottom */}
               <div style={{ marginTop: '8px', display: 'flex', gap: '4px', justifyContent: 'center' }}>
                 {/* Add to My Location Icon */}
                 {(() => {
                   const shouldShowButton = onAddToMyLocation && !position.isMyLocation && !position.isRouteMarker;
                   return shouldShowButton;
                 })() && (
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       onAddToMyLocation(position.info);
                     }}
                     style={{
                       backgroundColor: 'transparent',
                       color: '#28a745',
                       border: '2px solid #28a745',
                       borderRadius: '4px',
                       width: '28px',
                       height: '28px',
                       fontSize: '18px',
                       cursor: 'pointer',
                       fontWeight: 'bold',
                       transition: 'all 0.2s',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       padding: '0'
                     }}
                     onMouseOver={(e) => {
                       e.target.style.color = '#218838';
                       e.target.style.borderColor = '#218838';
                     }}
                     onMouseOut={(e) => {
                       e.target.style.color = '#28a745';
                       e.target.style.borderColor = '#28a745';
                     }}
                     title="Add to My Location"
                   >
                     📍
                   </button>
                 )}
                 
                 {/* PDF Icon */}
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     generateSingleLocationPDF(position.info);
                   }}
                   disabled={pdfLoadingStates.singleLocation}
                   style={{
                     backgroundColor: 'transparent',
                     color: pdfLoadingStates.singleLocation ? '#999' : '#dc3545',
                     border: pdfLoadingStates.singleLocation ? '2px solid #999' : '2px solid #dc3545',
                     borderRadius: '4px',
                     width: '28px',
                     height: '28px',
                     fontSize: '18px',
                     cursor: pdfLoadingStates.singleLocation ? 'not-allowed' : 'pointer',
                     fontWeight: 'bold',
                     transition: 'all 0.2s',
                     opacity: pdfLoadingStates.singleLocation ? 0.6 : 1,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     padding: '0'
                   }}
                   onMouseOver={(e) => {
                     if (!pdfLoadingStates.singleLocation) {
                       e.target.style.color = '#c82333';
                       e.target.style.borderColor = '#c82333';
                     }
                   }}
                   onMouseOut={(e) => {
                     if (!pdfLoadingStates.singleLocation) {
                       e.target.style.color = '#dc3545';
                       e.target.style.borderColor = '#dc3545';
                     }
                   }}
                   title="Generate PDF"
                 >
                   {pdfLoadingStates.singleLocation ? '⏳' : '📄'}
                 </button>
               </div>
           </div>
           </InfoWindow>
         );
       })}

      {/* Route Distance Information Info Window */}
      {routeDistanceInfo && shouldShowInfoWindow({ isRouteInfo: true }) && (() => {
        const routePosition = getRouteInfoPosition(routeDistanceInfo);
        return (
          <InfoWindow
            position={routePosition}
            onCloseClick={() => handleInfoWindowClose({ isRouteInfo: true })}
          >
          <div style={{ 
            padding: '6px 12px 12px 12px', 
            maxWidth: '300px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '2px solid #4285F4',
            position: 'relative'
          }}>

              <h4 style={{ 
                margin: '0 0 10px 0', 
                color: '#4285F4', 
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                Route Information
              </h4>

            <div style={{ fontSize: '12px', lineHeight: '1.4' }}>            
              <div style={{ marginBottom: '8px' }}>
                <strong>Road Distance:</strong> {routeDistanceInfo.roadDistance} km
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Road Duration:</strong> {routeDistanceInfo.roadDuration}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Aerial Distance:</strong> {routeDistanceInfo.aerialDistance ? Number(routeDistanceInfo.aerialDistance).toFixed(2) : '0.00'} km
              </div>
            </div>
            {/* Icon Row at Bottom */}
            <div style={{ marginTop: '8px', display: 'flex', gap: '4px', justifyContent: 'center' }}>
              {/* PDF Icon */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Find the destination marker to get its info
                  const destinationMarker = markerPositions.find(m => m.isRouteMarker);
                  if (destinationMarker) {
                    generateRoutePDF(destinationMarker.info, routeDistanceInfo);
                  }
                }}
                disabled={pdfLoadingStates.routePDF}
                style={{
                  backgroundColor: 'transparent',
                  color: pdfLoadingStates.routePDF ? '#999' : '#28a745',
                  border: pdfLoadingStates.routePDF ? '2px solid #999' : '2px solid #28a745',
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  fontSize: '18px',
                  cursor: pdfLoadingStates.routePDF ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  transition: 'all 0.2s',
                  opacity: pdfLoadingStates.routePDF ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0'
                }}
                onMouseOver={(e) => {
                  if (!pdfLoadingStates.routePDF) {
                    e.target.style.color = '#218838';
                    e.target.style.borderColor = '#218838';
                  }
                }}
                onMouseOut={(e) => {
                  if (!pdfLoadingStates.routePDF) {
                    e.target.style.color = '#28a745';
                    e.target.style.borderColor = '#28a745';
                  }
                }}
                title="Generate Route PDF"
              >
                {pdfLoadingStates.routePDF ? '⏳' : '📄'}
              </button>
            </div>
          </div>
        </InfoWindow>
        );
      })()}

      {/* Current Marker Info Window */}
      {(() => {
        const currentMarker = markerPositions.find(position => position.isCurrentMarker);
        return currentMarker && currentMarker.info && shouldShowInfoWindow(currentMarker);
      })() && (() => {
        const currentMarker = markerPositions.find(position => position.isCurrentMarker);
        
        // Calculate offset position for current marker's info window
        const getCurrentMarkerInfoWindowPosition = (markerPosition) => {
          const baseOffset = 0.0005;
          const zoomFactor = Math.max(1, currentZoom - 10);
          const dynamicOffset = baseOffset / zoomFactor;
          
          return {
            lat: markerPosition.lat + dynamicOffset,
            lng: markerPosition.lng
          };
        };
        
        return (
            <InfoWindow
              key="current-marker-info"
              position={getCurrentMarkerInfoWindowPosition(currentMarker)}
              onCloseClick={() => handleInfoWindowClose(currentMarker)}
            >
              <div style={{ 
                padding: '4px 8px 8px 8px', 
                maxWidth: '220px',
                backgroundColor: '#fff',
                borderRadius: '6px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                border: '2px solid #4285F4'
              }}>
                <h4 style={{ 
                  margin: '0 0 6px 0', 
                  color: '#4285F4',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}>
                  {currentMarker.title}
                </h4>
                <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
                  <div><strong>Address:</strong> {currentMarker.info.address}</div>
                  {currentMarker.info.digipin && currentMarker.info.digipin !== 'N/A' && (
                    <div><strong>Digipin:</strong> {currentMarker.info.digipin}</div>
                  )}
                  <div><strong>Coordinates:</strong> {currentMarker.info.coordinates}</div>
                </div>
                {/* Icon Row at Bottom */}
                <div style={{ marginTop: '8px', display: 'flex', gap: '4px', justifyContent: 'center' }}>
                  {/* Add to My Location Icon */}
                  {onAddToMyLocation && !currentMarker.isDefault && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToMyLocation(currentMarker.info);
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#28a745',
                        border: '2px solid #28a745',
                        borderRadius: '4px',
                        width: '28px',
                        height: '28px',
                        fontSize: '18px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.color = '#218838';
                        e.target.style.borderColor = '#218838';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = '#28a745';
                        e.target.style.borderColor = '#28a745';
                      }}
                      title="Add to My Location"
                    >
                      📍
                    </button>
                  )}
                  
                                      {/* Nearest Location Icon */}
                    {onNearestLocationClick && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Close current marker info window before calculating route
                          handleInfoWindowClose(currentMarker);
                          onNearestLocationClick();
                        }}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#4285F4',
                        border: '2px solid #4285F4',
                        borderRadius: '4px',
                        width: '28px',
                        height: '28px',
                        fontSize: '18px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.color = '#3367D6';
                        e.target.style.borderColor = '#3367D6';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = '#4285F4';
                        e.target.style.borderColor = '#4285F4';
                      }}
                      title="Nearest Location"
                    >
                      🎯
                    </button>
                  )}
                  
                                      {/* My Location Icon */}
                    {onSpecificLocationClick && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Close current marker info window before calculating route
                          handleInfoWindowClose(currentMarker);
                          onSpecificLocationClick();
                        }}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#34A853',
                        border: '2px solid #34A853',
                        borderRadius: '4px',
                        width: '28px',
                        height: '28px',
                        fontSize: '18px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.color = '#288533';
                        e.target.style.borderColor = '#288533';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = '#34A853';
                        e.target.style.borderColor = '#34A853';
                      }}
                      title="My Location"
                    >
                      🏠
                    </button>
                  )}
                  
                  {/* PDF Icon */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generateSingleLocationPDF(currentMarker.info);
                    }}
                    disabled={pdfLoadingStates.singleLocation}
                    style={{
                      backgroundColor: 'transparent',
                      color: pdfLoadingStates.singleLocation ? '#999' : '#dc3545',
                      border: pdfLoadingStates.singleLocation ? '2px solid #999' : '2px solid #dc3545',
                      borderRadius: '4px',
                      width: '28px',
                      height: '28px',
                      fontSize: '18px',
                      cursor: pdfLoadingStates.singleLocation ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      opacity: pdfLoadingStates.singleLocation ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0'
                    }}
                    onMouseOver={(e) => {
                      if (!pdfLoadingStates.singleLocation) {
                        e.target.style.color = '#c82333';
                        e.target.style.borderColor = '#c82333';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!pdfLoadingStates.singleLocation) {
                        e.target.style.color = '#dc3545';
                        e.target.style.borderColor = '#dc3545';
                      }
                    }}
                    title="Generate PDF"
                  >
                    {pdfLoadingStates.singleLocation ? '⏳' : '📄'}
                  </button>
                </div>
              </div>
                    </InfoWindow>
        );
      })()}

      {/* Route Marker - only show when route info is closed */}
      {routeDistanceInfo && !shouldShowInfoWindow({ isRouteInfo: true }) && (() => {
        const routePosition = getRouteInfoPosition(routeDistanceInfo);
        
        // Create green SVG icon for route marker with a different style
        const routeSvgIcon = {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#34A853" stroke="#ffffff" stroke-width="2"/>
              <path d="M12 6v6l4 2" stroke="#ffffff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 32)
        };
        
        return (
          <Marker
            key="route-marker"
            position={routePosition}
            icon={routeSvgIcon}
            title="Route Information"
            onClick={() => handleMarkerClick({ isRouteInfo: true })}
            options={{
              cursor: 'pointer'
            }}
          />
        );
      })()}

      {/* Current Marker - always show */}
      {(() => {
        const currentMarker = markerPositions.find(position => position.isCurrentMarker);
        return currentMarker;
      })() && (() => {
        const currentMarker = markerPositions.find(position => position.isCurrentMarker);
        
        // Create red SVG icon for current marker
        const currentMarkerSvgIcon = {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="48" height="48" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#FF0000"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 48)
        };
        
        return (
          <Marker
            key="current-marker"
            position={currentMarker}
            icon={currentMarkerSvgIcon}
            title={currentMarker.title}
            onClick={() => handleMarkerClick(currentMarker)}
            options={{
              cursor: 'pointer'
            }}
          />
        );
      })()}
    </GoogleMap>
  );
};

export default InteractiveMap;
